import React from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';

export type CosmeticBadgesLayout = 'corners' | 'perimeter' | 'grid';

export type Insets = { top?: number; right?: number; bottom?: number; left?: number };

type Props = {
  // Badge IDs to render. Duplicates are de-duped automatically.
  badgeIds: string[];
  // Renderer for each badge ID.
  renderBadge: (id: string) => React.ReactNode;
  // Overall positioning/layout strategy.
  layout?: CosmeticBadgesLayout;
  // Max badges to show (after de-duplication).
  max?: number;
  // Extra class names for the root overlay.
  className?: string;
  // If true (default), overlay ignores pointer events and is aria-hidden.
  decorative?: boolean;
  // Allow mouse/touch interaction for badges (overrides decorative).
  interactive?: boolean;
  // Add padding (px) on each side to avoid overlapping important UI (e.g., tier chip).
  safeArea?: Insets;
  // Grid config when layout='grid'
  grid?: { cols?: number; rows?: number };
  // Animation controls
  animate?: boolean; // default true
  popInDurationMs?: number; // default 160
  // Called once when a badge first appears (useful for analytics).
  onBadgeShow?: (id: string, index: number) => void;
};

const defaultSafeArea: Required<Insets> = { top: 8, right: 8, bottom: 8, left: 8 };

function mergeInsets(a?: Insets, b: Required<Insets> = defaultSafeArea): Required<Insets> {
  return {
    top: a?.top ?? b.top,
    right: a?.right ?? b.right,
    bottom: a?.bottom ?? b.bottom,
    left: a?.left ?? b.left,
  };
}

function uniqueIds(ids: string[], max?: number) {
  const set = new Set<string>();
  const out: string[] = [];
  for (const id of ids) {
    if (!set.has(id)) {
      set.add(id);
      out.push(id);
      if (max && out.length >= max) break;
    }
  }
  return out;
}

// Static anchors for 'corners' layout (up to 10 sensible spots)
const CORNER_ANCHORS: Array<React.CSSProperties> = [
  // corners
  { top: 8, left: 8 },
  { top: 8, right: 8 },
  { bottom: 8, left: 8 },
  { bottom: 8, right: 8 },
  // centers on sides
  { top: 8, left: '50%', transform: 'translateX(-50%)' },
  { bottom: 8, left: '50%', transform: 'translateX(-50%)' },
  { top: '50%', left: 8, transform: 'translateY(-50%)' },
  { top: '50%', right: 8, transform: 'translateY(-50%)' },
  // quarter points on top/bottom
  { top: 8, left: '25%' },
  { top: 8, left: '75%' },
];

// Compute perimeter positions (distribute evenly around the edges).
function computePerimeterAnchors(count: number, safe: Required<Insets>): Array<React.CSSProperties> {
  const anchors: Array<React.CSSProperties> = [];
  if (count <= 0) return anchors;

  for (let i = 0; i < count; i++) {
    const t = i / count; // 0..1
    const seg = t * 4; // four edges
    if (seg < 1) {
      const p = seg; // top edge 0..1
      anchors.push({ top: safe.top, left: `calc(${p * 100}% - 0px)` });
    } else if (seg < 2) {
      const p = seg - 1; // right edge 0..1
      anchors.push({ top: `calc(${p * 100}% - 0px)`, right: safe.right });
    } else if (seg < 3) {
      const p = seg - 2; // bottom edge 0..1
      anchors.push({ bottom: safe.bottom, left: `calc(${(1 - p) * 100}% - 0px)` });
    } else {
      const p = seg - 3; // left edge 0..1
      anchors.push({ top: `calc(${(1 - p) * 100}% - 0px)`, left: safe.left });
    }
  }
  return anchors;
}

// Compute grid anchors
function computeGridAnchors(count: number, safe: Required<Insets>, cols = 3, rows = 2): Array<React.CSSProperties> {
  const anchors: Array<React.CSSProperties> = [];
  const total = Math.min(count, cols * rows);
  for (let i = 0; i < total; i++) {
    const r = Math.floor(i / cols);
    const c = i % cols;
    const x = (c + 0.5) / cols; // 0..1
    const y = (r + 0.5) / rows; // 0..1
    anchors.push({
      left: `calc(${x * 100}% - 0px)`,
      top: `calc(${y * 100}% - 0px)`,
      transform: 'translate(-50%, -50%)',
    });
  }
  return anchors;
}

const popVariants = {
  initial: { opacity: 0, scale: 0.8, y: 4 },
  animate: { opacity: 1, scale: 1, y: 0 },
  exit: { opacity: 0, scale: 0.92, y: 4 },
};

export function CosmeticBadgesOverlay({
  badgeIds,
  renderBadge,
  layout = 'corners',
  max,
  className,
  decorative = true,
  interactive = false,
  safeArea,
  grid,
  animate = true,
  popInDurationMs = 160,
  onBadgeShow,
}: Props) {
  const reduceMotion = useReducedMotion();
  const ids = React.useMemo(() => uniqueIds(badgeIds, max), [badgeIds, max]);
  const safe = React.useMemo(() => mergeInsets(safeArea), [safeArea]);

  // Track which ids have been announced for onBadgeShow
  const seen = React.useRef<Set<string>>(new Set());
  React.useEffect(() => {
    // remove ids that are no longer present so they can be re-announced later if needed
    for (const s of Array.from(seen.current)) {
      if (!ids.includes(s)) seen.current.delete(s);
    }
  }, [ids]);

  const anchors = React.useMemo(() => {
    if (layout === 'corners') {
      // Map anchors but shift by safeArea when numeric values exist
      return CORNER_ANCHORS.slice(0, ids.length).map((style) => {
        const s: React.CSSProperties = { ...style };
        if (typeof s.top === 'number') s.top = (s.top as number) + safe.top;
        if (typeof s.left === 'number') s.left = (s.left as number) + safe.left;
        if (typeof s.right === 'number') s.right = (s.right as number) + safe.right;
        if (typeof s.bottom === 'number') s.bottom = (s.bottom as number) + safe.bottom;
        return s;
      });
    }
    if (layout === 'perimeter') {
      return computePerimeterAnchors(ids.length, safe);
    }
    // grid
    return computeGridAnchors(ids.length, safe, grid?.cols ?? 3, grid?.rows ?? 2);
  }, [ids.length, layout, grid?.cols, grid?.rows, safe]);

  const rootStyle: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    pointerEvents: interactive ? 'auto' : 'none',
  };

  const ariaHidden = decorative && !interactive;

  return (
    <div className={['relative', className || ''].join(' ')} style={rootStyle} aria-hidden={ariaHidden}>
      <AnimatePresence initial={false}>
        {ids.map((id, i) => {
          const style = anchors[i] || {};
          const delay = animate && !reduceMotion ? i * 0.03 : 0;

          const handleAnimationComplete = () => {
            if (!seen.current.has(id)) {
              seen.current.add(id);
              onBadgeShow?.(id, i);
            }
          };

          const content = renderBadge(id);

          return (
            <motion.div
              key={id}
              className="absolute"
              style={style}
              variants={popVariants}
              initial={animate && !reduceMotion ? 'initial' : false}
              animate={animate && !reduceMotion ? 'animate' : false}
              exit={animate && !reduceMotion ? 'exit' : undefined}
              transition={{ duration: popInDurationMs / 1000, ease: 'easeOut', delay }}
              onAnimationComplete={handleAnimationComplete}
            >
              {/* When interactive, allow pointer events for the badge itself */}
              <div style={{ pointerEvents: interactive ? 'auto' : 'none' }}>{content}</div>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}

export default CosmeticBadgesOverlay;